import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { mapSupabaseError, getUserFriendlyMessage } from '../lib/errors';
import { captureError } from '../lib/sentry';
import { useToast } from '../components/Toast';
import { DEFAULT_CURRENCY_RATES } from '../lib/constants';

/**
 * ნივთების მართვის hooks - სრული error handling-ით
 */

// ნივთების მიღება
export function useItems(filters = {}) {
  const toast = useToast();
  const queryKey = ['items', filters];

  const { data: items, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      let query = supabase
        .from('items')
        .select('*')
        .order('created_at', { ascending: false });

      // სერვერის მხარეს ფილტრაცია
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters.platform && filters.platform !== 'all') {
        query = query.eq('platform', filters.platform);
      }

      const { data, error } = await query;

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, { 
          tags: { 
            feature: 'items', 
            action: 'fetch',
            status: filters.status || 'all'
          } 
        });
        throw apiError;
      }

      // კლიენტის მხარეს ძიება (მცირე მონაცემებისთვის)
      if (filters.search && data) {
        const searchLower = filters.search.toLowerCase();
        return data.filter(item =>
          item.item_number?.toLowerCase().includes(searchLower) ||
          item.name?.toLowerCase().includes(searchLower) ||
          item.tracking_code?.toLowerCase().includes(searchLower) ||
          item.client_name?.toLowerCase().includes(searchLower)
        );
      }

      return data;
    },
    staleTime: 1000 * 60 * 2, // 2 წუთი
    gcTime: 1000 * 60 * 10, // 10 წუთი
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      toast.error(getUserFriendlyMessage(apiError));
    },
  });

  return { items, isLoading, error, refetch };
}

// ახალი ნივთის დამატება
export function useAddItem() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (itemData) => {
      // თუ purchase_price_gel არ არის, გამოვთვალოთ
      if (!itemData.purchase_price_gel && itemData.purchase_price_usd && itemData.currency) {
        const rate = itemData.currency === 'USD' ? DEFAULT_CURRENCY_RATES.USD : DEFAULT_CURRENCY_RATES.EUR;
        itemData.purchase_price_gel = itemData.purchase_price_usd * rate;
      }

      const { data, error } = await supabase
        .from('items')
        .insert([itemData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      captureError(apiError, {
        tags: { feature: 'items', action: 'create' },
        extra: { itemData }
      });
      toast.error(getUserFriendlyMessage(apiError));
    },
    onSuccess: () => {
      toast.success('ნივთი წარმატებით დაემატა!');
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['account-limits'] });
    },
  });
}

// ნივთის განახლება
export function useUpdateItem() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const { data: result, error } = await supabase
        .from('items')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onError: (error, { id, data }) => {
      const apiError = mapSupabaseError(error);
      captureError(apiError, { 
        tags: { feature: 'items', action: 'update' },
        extra: { itemId: id, updatedData: data }
      });
      toast.error(getUserFriendlyMessage(apiError));
    },
    onSuccess: (updatedItem) => {
      toast.success('ნივთი განახლდა!');
      
      // განვაახლოთ კონკრეტული ნივთი cache-ში
      queryClient.setQueryData(['items'], (old) => {
        if (!old) return old;
        return old.map(item => item.id === updatedItem.id ? updatedItem : item);
      });

      // ინვალიდაცია
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });
}

// ნივთის წაშლა (soft delete) - Optimistic Updates-ით
export function useDeleteItem() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.rpc('delete_item', {
        p_id: id,
        p_reason: 'მომხმარებლის მიერ წაშლა'
      });

      if (error) throw error;
      return id;
    },
    // Optimistic update - წავშალოთ cache-დან დაუყოვნებლივ
    onMutate: async (deletedId) => {
      // გავაუქმოთ მიმდინარე queries
      await queryClient.cancelQueries({ queryKey: ['items'] });
      
      // შევინახოთ წინა მდგომარეობა rollback-ისთვის
      const previousItems = queryClient.getQueryData(['items']);
      
      // განვაახლოთ cache - წავშალოთ ნივთი
      queryClient.setQueryData(['items'], (old) => {
        if (!old) return old;
        return old.filter(item => item.id !== deletedId);
      });
      
      return { previousItems };
    },
    onError: (error, id, context) => {
      // Rollback on error - აღვადგინოთ წინა მდგომარეობა
      if (context?.previousItems) {
        queryClient.setQueryData(['items'], context.previousItems);
      }
      
      const apiError = mapSupabaseError(error);
      captureError(apiError, { 
        tags: { feature: 'items', action: 'delete' },
        extra: { itemId: id }
      });
      toast.error(getUserFriendlyMessage(apiError));
    },
    onSuccess: (deletedId) => {
      toast.success('ნივთი წაიშალა!');
      
      // ინვალიდაცია
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['account-limits'] });
    },
  });
}

// ნივთის მიღება ID-ით
export function useItem(itemId) {
  const toast = useToast();
  
  return useQuery({
    queryKey: ['item', itemId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('items')
        .select('*')
        .eq('id', itemId)
        .single();

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, { 
          tags: { feature: 'items', action: 'fetch_by_id' },
          extra: { itemId }
        });
        throw apiError;
      }
      return data;
    },
    enabled: !!itemId,
    staleTime: 1000 * 60 * 5,
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      toast.error(getUserFriendlyMessage(apiError));
    },
  });
}
